
import { ErrorBoundary } from './errorBoundary.js';

// Use global logger to avoid circular dependencies
const log = typeof window !== 'undefined' && window.Log ? window.Log('validation') : {
  blue: () => {}, red: () => {}, green: () => {}, yellow: () => {}, purple: () => {}, log: () => {}, table: () => {}
};

/**
 * Validation error class for structured error handling
 */
export class ValidationError extends Error {
  constructor(message, field = null, value = null) {
    super(message);
    this.name = 'ValidationError';
    this.field = field;
    this.value = value;
    this.timestamp = new Date().toISOString();
  }
}

/**
 * Validation utilities and schemas
 */
export class Validator {
  
  /**
   * Validate a single cocktail object
   * @param {object} cocktail - The cocktail to validate
   * @returns {object} Validated and cleaned cocktail
   * @throws {ValidationError} If validation fails
   */
  static validateCocktail(cocktail) {
    const errors = [];
    
    if (!cocktail || typeof cocktail !== 'object') {
      throw new ValidationError('Cocktail must be a valid object', 'cocktail', cocktail);
    }
    
    // Required fields validation
    const requiredFields = [
      { field: 'ID', type: 'string', message: 'Cocktail must have a valid ID' },
      { field: 'name', type: 'string', message: 'Cocktail must have a valid name' }
    ];
    
    for (const { field, type, message } of requiredFields) {
      const value = cocktail[field];
      
      if (!value) {
        errors.push(`${message} (missing ${field})`);
        continue;
      }
      
      if (typeof value !== type) {
        errors.push(`${message} (${field} must be ${type}, got ${typeof value})`);
        continue;
      }
      
      if (type === 'string' && value.trim().length === 0) {
        errors.push(`${message} (${field} cannot be empty)`);
      }
    }
    
    // Optional fields validation with type checking
    const optionalFields = [
      { field: 'category', type: 'string' },
      { field: 'glass', type: 'string' },
      { field: 'alcoholic', type: 'string' },
      { field: 'instructions', type: 'string' },
      { field: 'ingredients', type: 'string' },
      { field: 'image', type: 'string' }
    ];
    
    for (const { field, type } of optionalFields) {
      const value = cocktail[field];
      if (value !== undefined && value !== null && typeof value !== type) {
        errors.push(`${field} must be ${type} if provided, got ${typeof value}`);
      }
    }
    
    // Image URL validation if provided
    if (cocktail.image && typeof cocktail.image === 'string') {
      if (!this.isValidUrl(cocktail.image)) {
        errors.push('Image must be a valid URL');
      }
    }
    
    // Name length validation
    if (cocktail.name && cocktail.name.length > 100) {
      errors.push('Cocktail name cannot exceed 100 characters');
    }
    
    // ID format validation
    if (cocktail.ID && !/^[a-zA-Z0-9\-_]+$/.test(cocktail.ID)) {
      errors.push('Cocktail ID must contain only alphanumeric characters, hyphens, and underscores');
    }
    
    if (errors.length > 0) {
      const error = new ValidationError(
        `Cocktail validation failed: ${errors.join('; ')}`,
        'cocktail',
        cocktail
      );
      error.validationErrors = errors;
      throw error;
    }
    
    // Return cleaned cocktail with sanitized strings
    const cleaned = {
      ID: this.sanitizeString(cocktail.ID),
      name: this.sanitizeString(cocktail.name),
      category: cocktail.category ? this.sanitizeString(cocktail.category) : null,
      glass: cocktail.glass ? this.sanitizeString(cocktail.glass) : null,
      alcoholic: cocktail.alcoholic ? this.sanitizeString(cocktail.alcoholic) : null,
      instructions: cocktail.instructions ? this.sanitizeString(cocktail.instructions) : null,
      ingredients: cocktail.ingredients ? this.sanitizeString(cocktail.ingredients) : null,
      image: cocktail.image ? this.sanitizeString(cocktail.image) : null
    };
    
    log.blue('Validated cocktail:', cleaned.name);
    return cleaned;
  }
  
  /**
   * Validate an array of cocktails
   * @param {array} cocktails - Array of cocktails to validate
   * @returns {array} Array of validated cocktails
   */
  static validateCocktails(cocktails) {
    if (!Array.isArray(cocktails)) {
      throw new ValidationError('Cocktails must be an array', 'cocktails', cocktails);
    }
    
    const validated = [];
    const errors = [];
    
    cocktails.forEach((cocktail, index) => {
      try {
        const validatedCocktail = this.validateCocktail(cocktail);
        validated.push(validatedCocktail);
      } catch (error) {
        errors.push({ index, error: error.message, cocktail });
        log.yellow(`Skipping invalid cocktail at index ${index}:`, error.message);
      }
    });
    
    if (errors.length > 0) {
      log.yellow(`Validation completed with ${errors.length} errors out of ${cocktails.length} cocktails`);
      
      // Report validation errors but don't throw - return valid cocktails
      ErrorBoundary.reportError(
        new ValidationError(`${errors.length} cocktails failed validation`),
        'cocktail-validation',
        { errors, totalCocktails: cocktails.length }
      );
    }
    
    log.green(`Validated ${validated.length} cocktails successfully`);
    return validated;
  }
  
  /**
   * Validate cocktail ID
   * @param {string} cocktailId - The cocktail ID to validate
   * @returns {string} Validated cocktail ID
   * @throws {ValidationError} If validation fails
   */
  static validateCocktailId(cocktailId) {
    if (!cocktailId) {
      throw new ValidationError('Cocktail ID is required', 'cocktailId', cocktailId);
    }
    
    if (typeof cocktailId !== 'string') {
      throw new ValidationError('Cocktail ID must be a string', 'cocktailId', cocktailId);
    }
    
    if (cocktailId.trim().length === 0) {
      throw new ValidationError('Cocktail ID cannot be empty', 'cocktailId', cocktailId);
    }
    
    if (cocktailId.length > 50) {
      throw new ValidationError('Cocktail ID cannot exceed 50 characters', 'cocktailId', cocktailId);
    }
    
    const cleaned = this.sanitizeString(cocktailId);
    return cleaned;
  }
  
  /**
   * Validate search term
   * @param {string} searchTerm - The search term to validate
   * @returns {string} Validated search term
   */
  static validateSearchTerm(searchTerm) {
    if (searchTerm === null || searchTerm === undefined) {
      return '';
    }
    
    if (typeof searchTerm !== 'string') {
      log.yellow('Search term must be string, converting:', typeof searchTerm);
      searchTerm = String(searchTerm);
    }
    
    // Limit search term length
    if (searchTerm.length > 100) {
      log.yellow('Search term truncated to 100 characters');
      searchTerm = searchTerm.substring(0, 100);
    }
    
    return this.sanitizeString(searchTerm);
  }
  
  /**
   * Validate localStorage data
   * @param {string} key - The localStorage key
   * @param {any} data - The data to validate
   * @returns {any} Validated data or default
   */
  static validateStorageData(key, data) {
    try {
      switch (key) {
        case 'cocktail-favorites':
          return this.validateFavoritesData(data);
        default:
          log.yellow(`Unknown storage key for validation: ${key}`);
          return data;
      }
    } catch (error) {
      log.red(`Storage data validation failed for ${key}:`, error);
      ErrorBoundary.reportError(error, 'storage-validation');
      return this.getStorageDefault(key);
    }
  }
  
  /**
   * Validate favorites data from localStorage
   * @param {any} data - The favorites data to validate
   * @returns {Set} Validated favorites set
   */
  static validateFavoritesData(data) {
    if (!data) {
      return new Set();
    }
    
    let parsed;
    if (typeof data === 'string') {
      try {
        parsed = JSON.parse(data);
      } catch (error) {
        log.yellow('Invalid JSON in favorites data, resetting');
        return new Set();
      }
    } else {
      parsed = data;
    }
    
    if (!Array.isArray(parsed)) {
      log.yellow('Favorites data must be array, resetting');
      return new Set();
    }
    
    // Filter out invalid IDs
    const validIds = parsed.filter(id => {
      if (typeof id !== 'string') return false;
      if (id.trim().length === 0) return false;
      if (id.length > 50) return false;
      return true;
    });
    
    if (validIds.length !== parsed.length) {
      log.yellow(`Filtered out ${parsed.length - validIds.length} invalid favorite IDs`);
    }
    
    return new Set(validIds.map(id => this.sanitizeString(id)));
  }
  
  /**
   * Get default value for storage keys
   * @param {string} key - The storage key
   * @returns {any} Default value
   */
  static getStorageDefault(key) {
    const defaults = {
      'cocktail-favorites': new Set()
    };
    
    return defaults[key] || null;
  }
  
  /**
   * Validate API response structure
   * @param {any} response - The API response to validate
   * @returns {object} Validated response
   * @throws {ValidationError} If validation fails
   */
  static validateApiResponse(response) {
    if (!response || typeof response !== 'object') {
      throw new ValidationError('API response must be a valid object', 'response', response);
    }
    
    if (!response.data) {
      throw new ValidationError('API response must contain data field', 'data', response);
    }
    
    if (!Array.isArray(response.data)) {
      throw new ValidationError('API response data must be an array', 'data', response.data);
    }
    
    if (response.data.length === 0) {
      log.yellow('API response contains no data');
    }
    
    return {
      data: this.validateCocktails(response.data),
      metadata: {
        totalCount: response.data.length,
        validatedCount: response.data.length,
        validatedAt: new Date().toISOString()
      }
    };
  }
  
  /**
   * Sanitize string input to prevent XSS and normalize data
   * @param {string} str - String to sanitize
   * @returns {string} Sanitized string
   */
  static sanitizeString(str) {
    if (typeof str !== 'string') return str;
    
    return str
      .trim()
      // Remove dangerous control characters but preserve \n, \r, and \t
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g, '')
      // Normalize multiple spaces but preserve line breaks
      .replace(/[ \t]+/g, ' ');
  }
  
  /**
   * Validate URL format
   * @param {string} url - URL to validate
   * @returns {boolean} True if valid URL
   */
  static isValidUrl(url) {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }
  
  /**
   * Escape HTML content to prevent XSS attacks
   * Consolidates duplicate escapeHtml implementations from multiple components
   * @param {any} text - Text to escape
   * @returns {string} HTML-escaped text
   */
  static escapeHtml(text) {
    if (text === null || text === undefined) {
      return '';
    }
    
    if (typeof text !== 'string') {
      text = String(text);
    }
    
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
  
  /**
   * Validate component state
   * @param {object} state - State object to validate
   * @returns {object} Validated state
   */
  static validateState(state) {
    const errors = [];
    
    // Validate cocktails array
    if (state.cocktails !== undefined) {
      if (!Array.isArray(state.cocktails)) {
        errors.push('cocktails must be an array');
      }
    }
    
    // Validate selected cocktail
    if (state.selectedCocktail !== undefined && state.selectedCocktail !== null) {
      try {
        this.validateCocktail(state.selectedCocktail);
      } catch (error) {
        errors.push(`selectedCocktail validation failed: ${error.message}`);
      }
    }
    
    // Validate search term
    if (state.searchTerm !== undefined) {
      try {
        state.searchTerm = this.validateSearchTerm(state.searchTerm);
      } catch (error) {
        errors.push(`searchTerm validation failed: ${error.message}`);
      }
    }
    
    // Validate boolean flags
    const booleanFields = ['loading', 'showFavoritesOnly'];
    booleanFields.forEach(field => {
      if (state[field] !== undefined && typeof state[field] !== 'boolean') {
        errors.push(`${field} must be boolean`);
      }
    });
    
    // Validate error field
    if (state.error !== undefined && state.error !== null && typeof state.error !== 'string') {
      errors.push('error must be string or null');
    }
    
    // Validate favorites
    if (state.favorites !== undefined && !(state.favorites instanceof Set)) {
      errors.push('favorites must be a Set');
    }
    
    if (errors.length > 0) {
      log.yellow('State validation errors:', errors);
      ErrorBoundary.reportError(
        new ValidationError(`State validation failed: ${errors.join('; ')}`),
        'state-validation',
        { state, errors }
      );
    }
    
    return state;
  }
}

/**
 * Validation helper functions for common use cases
 */
export const ValidationHelpers = {
  
  /**
   * Safe cocktail getter that validates on access
   * @param {object} cocktail - Raw cocktail object
   * @returns {object|null} Validated cocktail or null
   */
  safeCocktail(cocktail) {
    try {
      return Validator.validateCocktail(cocktail);
    } catch (error) {
      log.yellow('Invalid cocktail data:', error.message);
      return null;
    }
  },
  
  /**
   * Safe ID extraction with validation
   * @param {object} cocktail - Cocktail object
   * @returns {string|null} Valid ID or null
   */
  safeCocktailId(cocktail) {
    try {
      const id = cocktail?.ID || cocktail?.idDrink;
      return id ? Validator.validateCocktailId(id) : null;
    } catch (error) {
      log.yellow('Invalid cocktail ID:', error.message);
      return null;
    }
  },
  
  /**
   * Safe localStorage access with validation
   * @param {string} key - Storage key
   * @returns {any} Validated data or default
   */
  safeStorageGet(key) {
    try {
      const raw = localStorage.getItem(key);
      return Validator.validateStorageData(key, raw);
    } catch (error) {
      log.yellow(`Storage access failed for ${key}:`, error.message);
      return Validator.getStorageDefault(key);
    }
  },
  
  /**
   * Safe cocktail setter with feedback for components
   * @param {object} cocktail - Cocktail object to validate
   * @param {string} componentName - Name of the component for error reporting
   * @returns {object|null} Validated cocktail or null
   */
  safeCocktailWithFeedback(cocktail, componentName) {
    try {
      const validated = this.safeCocktail(cocktail);
      if (!validated && cocktail) {
        log.yellow(`Invalid cocktail data provided to ${componentName}`);
        ErrorBoundary.reportError(
          new ValidationError(`Invalid cocktail data provided to ${componentName}`, 'cocktailData', cocktail),
          componentName
        );
      }
      return validated;
    } catch (error) {
      log.red(`Cocktail validation error in ${componentName}:`, error.message);
      ErrorBoundary.reportError(error, componentName);
      return null;
    }
  },
  
  /**
   * Validate component props/attributes
   * @param {object} props - Props object to validate
   * @param {object} schema - Validation schema
   * @param {string} componentName - Component name for error reporting
   * @returns {object} Validated props
   */
  validateComponentProps(props, schema, componentName) {
    const validated = {};
    const errors = [];
    
    // Check required properties
    if (schema.required) {
      schema.required.forEach(key => {
        if (!(key in props)) {
          errors.push(`Missing required prop: ${key}`);
        }
      });
    }
    
    // Validate each property
    Object.keys(props).forEach(key => {
      const value = props[key];
      const rule = schema.properties && schema.properties[key];
      
      if (!rule) {
        // Unknown prop, warn but allow
        log.yellow(`Unknown prop '${key}' in ${componentName}`);
        validated[key] = value;
        return;
      }
      
      try {
        validated[key] = this.validateProp(value, rule, key);
      } catch (error) {
        errors.push(`Invalid prop '${key}': ${error.message}`);
      }
    });
    
    if (errors.length > 0) {
      const error = new ValidationError(
        `Component validation failed: ${errors.join('; ')}`,
        'componentProps',
        props
      );
      error.componentName = componentName;
      error.validationErrors = errors;
      throw error;
    }
    
    return validated;
  },
  
  /**
   * Validate a single prop value
   * @param {any} value - Value to validate
   * @param {object} rule - Validation rule
   * @param {string} propName - Property name
   * @returns {any} Validated value
   */
  validateProp(value, rule, propName) {
    // Handle null/undefined
    if (value == null) {
      if (rule.required) {
        throw new ValidationError(`${propName} is required`);
      }
      return rule.default !== undefined ? rule.default : value;
    }
    
    // Type validation
    if (rule.type && typeof value !== rule.type) {
      // Try type coercion for common cases
      if (rule.type === 'string') {
        value = String(value);
      } else if (rule.type === 'number') {
        const num = Number(value);
        if (isNaN(num)) {
          throw new ValidationError(`${propName} must be a number`);
        }
        value = num;
      } else if (rule.type === 'boolean') {
        value = Boolean(value);
      } else {
        throw new ValidationError(`${propName} must be of type ${rule.type}`);
      }
    }
    
    // String validation
    if (rule.type === 'string' && typeof value === 'string') {
      if (rule.minLength && value.length < rule.minLength) {
        throw new ValidationError(`${propName} must be at least ${rule.minLength} characters`);
      }
      if (rule.maxLength && value.length > rule.maxLength) {
        throw new ValidationError(`${propName} must be no more than ${rule.maxLength} characters`);
      }
      if (rule.pattern && !new RegExp(rule.pattern).test(value)) {
        throw new ValidationError(`${propName} does not match required pattern`);
      }
      
      // Sanitize string
      value = Validator.sanitizeString(value);
    }
    
    // Number validation
    if (rule.type === 'number' && typeof value === 'number') {
      if (rule.min !== undefined && value < rule.min) {
        throw new ValidationError(`${propName} must be at least ${rule.min}`);
      }
      if (rule.max !== undefined && value > rule.max) {
        throw new ValidationError(`${propName} must be no more than ${rule.max}`);
      }
    }
    
    // Array validation
    if (rule.type === 'array' && Array.isArray(value)) {
      if (rule.minItems && value.length < rule.minItems) {
        throw new ValidationError(`${propName} must have at least ${rule.minItems} items`);
      }
      if (rule.maxItems && value.length > rule.maxItems) {
        throw new ValidationError(`${propName} must have no more than ${rule.maxItems} items`);
      }
    }
    
    // Custom validator
    if (rule.validator) {
      try {
        const result = rule.validator(value);
        if (result !== true && typeof result === 'string') {
          throw new ValidationError(result);
        }
      } catch (error) {
        throw new ValidationError(`Custom validation failed: ${error.message}`);
      }
    }
    
    return value;
  }
};

// Global error handler for validation errors
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (event.error instanceof ValidationError) {
      log.red('Validation error caught:', event.error.message);
      ErrorBoundary.reportError(event.error, 'global-validation');
    }
  });
}

export default Validator;
